"use client";

import * as React from "react";
import * as RDialog from "@radix-ui/react-dialog";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "./cn";

export interface ImageLightboxLabels {
  /** Aria-label for the close button. */
  close: string;
  /** Aria-label for the previous-image button. */
  prev?: string;
  /** Aria-label for the next-image button. */
  next?: string;
}

export interface ImageLightboxProps {
  /** Image URLs in display order. */
  images: string[];
  /** Index of the image to show, or null when closed. */
  index: number | null;
  /** Called when the lightbox should close. */
  onClose: () => void;
  /** Called when navigating to another image. Omit to disable prev/next. */
  onIndexChange?: (next: number) => void;
  labels: ImageLightboxLabels;
}

// Full-screen image preview. Built on Radix Dialog for focus trap, escape +
// scroll lock. Shared by LogoUploader (single image) and ImageGalleryEditor
// (navigable). Navigation keys work only when onIndexChange is provided.
export function ImageLightbox({
  images,
  index,
  onClose,
  onIndexChange,
  labels,
}: ImageLightboxProps) {
  const open = index !== null;
  const canNavigate = !!onIndexChange && images.length > 1;

  const go = React.useCallback(
    (delta: number) => {
      if (index === null || !onIndexChange) return;
      const next = (index + delta + images.length) % images.length;
      onIndexChange(next);
    },
    [index, images.length, onIndexChange],
  );

  React.useEffect(() => {
    if (!open || !canNavigate) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, canNavigate, go]);

  const src = index !== null ? images[index] : undefined;

  return (
    <RDialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in" />
        <RDialog.Content
          aria-describedby={undefined}
          onClick={onClose}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=open]:zoom-in-95"
        >
          <RDialog.Title className="sr-only">{labels.close}</RDialog.Title>

          {src ? (
            <img
              src={src}
              alt=""
              onClick={(e) => e.stopPropagation()}
              className="max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] rounded-xl object-contain shadow-2xl"
            />
          ) : null}

          <RDialog.Close
            aria-label={labels.close}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-paper/15 text-paper backdrop-blur transition-colors hover:bg-paper/30"
          >
            <X size={20} strokeWidth={2} />
          </RDialog.Close>

          {canNavigate ? (
            <>
              <NavButton
                side="left"
                label={labels.prev}
                onClick={(e) => {
                  e.stopPropagation();
                  go(-1);
                }}
              />
              <NavButton
                side="right"
                label={labels.next}
                onClick={(e) => {
                  e.stopPropagation();
                  go(1);
                }}
              />
              <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-ink/60 px-3 py-1 text-sm font-semibold tabular-nums text-paper">
                {(index ?? 0) + 1} / {images.length}
              </span>
            </>
          ) : null}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  );
}

function NavButton({
  side,
  label,
  onClick,
}: {
  side: "left" | "right";
  label?: string;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-paper/15 text-paper backdrop-blur transition-colors hover:bg-paper/30",
        side === "left" ? "left-4" : "right-4",
      )}
    >
      {side === "left" ? (
        <ChevronLeft size={24} strokeWidth={2} />
      ) : (
        <ChevronRight size={24} strokeWidth={2} />
      )}
    </button>
  );
}
